/**
 * Image Upload API Service
 * Handles S3 presigned URL generation and image uploads
 */

import { fetchWithAuthRetry } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
export const MAX_IMAGE_SIZE_LABEL = '10 MB';

export interface UploadedImage {
  url: string;
  key: string;
  filename: string;
  size: number;
  contentType: string;
  storagePrefix: string;
}

export interface PresignedUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  key: string;
  storagePrefix: string;
}

/**
 * Request a presigned URL for uploading an image to S3
 */
export async function getPresignedUploadUrl(
  filename: string,
  contentType: string,
  options?: { storagePrefix?: string }
): Promise<PresignedUrlResponse> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/images/presigned-url`, {
    method: 'POST',
    body: JSON.stringify({ filename, contentType, storagePrefix: options?.storagePrefix }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get presigned URL: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Request a presigned URL for uploading a file to S3
 * Uses the /api/files/presigned-url endpoint for non-image file types
 */
export async function getPresignedFileUploadUrl(
  filename: string,
  contentType: string
): Promise<PresignedUrlResponse> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/files/presigned-url`, {
    method: 'POST',
    body: JSON.stringify({ filename, contentType }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get presigned URL: ${response.statusText}`);
  }

  return response.json();
}

const MAX_PDF_SIZE = 50 * 1024 * 1024; // 50MB

/**
 * Upload a PDF file and return the public URL
 * Validates file type and size before uploading
 */
export async function uploadPdf(file: File): Promise<UploadedImage> {
  if (file.type !== 'application/pdf') {
    throw new Error('Invalid file type. Only PDF files are accepted.');
  }

  if (file.size > MAX_PDF_SIZE) {
    throw new Error('File too large. Maximum size is 50MB.');
  }

  const { uploadUrl, publicUrl, key, storagePrefix } = await getPresignedFileUploadUrl(
    file.name,
    file.type
  );

  await uploadToS3(uploadUrl, file);

  return {
    url: publicUrl,
    key,
    filename: file.name,
    size: file.size,
    contentType: file.type,
    storagePrefix,
  };
}

/**
 * Upload a file directly to S3 using a presigned URL
 */
export async function uploadToS3(
  presignedUrl: string,
  file: File
): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to upload to S3: ${response.statusText}`);
  }
}

/**
 * Upload an image file and return the public URL
 */
export async function uploadImage(file: File, options?: { storagePrefix?: string }): Promise<UploadedImage> {
  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Invalid file type. Allowed: JPEG, PNG, GIF, WebP');
  }

  // Validate file size (max 10 MB)
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    throw new Error(`File too large. Maximum size is ${MAX_IMAGE_SIZE_LABEL}`);
  }

  // Get presigned URL
  const { uploadUrl, publicUrl, key, storagePrefix } = await getPresignedUploadUrl(
    file.name,
    file.type,
    options
  );

  // Upload to S3
  await uploadToS3(uploadUrl, file);

  return {
    url: publicUrl,
    key,
    filename: file.name,
    size: file.size,
    contentType: file.type,
    storagePrefix,
  };
}

/**
 * Convert a File to base64 data URL (for preview before upload)
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Upload a Blob directly to S3 using a presigned URL
 * Used for generated images and other in-memory image sources
 */
async function uploadBlobToS3(
  presignedUrl: string,
  blob: Blob,
  contentType: string
): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: blob,
    headers: {
      'Content-Type': contentType,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to upload to S3: ${response.statusText}`);
  }
}

/**
 * Validate image content type
 * Requirements: 6.5
 */
export function isValidImageType(contentType: string): boolean {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  return allowedTypes.includes(contentType);
}

/**
 * Upload an image blob and return the public URL
 * Used for images from AI generation and other in-memory image sources
 * Requirements: 6.1, 6.2, 6.3
 */
export async function uploadImageBlob(
  blob: Blob,
  filename: string,
  contentType: string,
  options?: { storagePrefix?: string }
): Promise<UploadedImage> {
  // Validate content type
  if (!isValidImageType(contentType)) {
    throw new Error('Invalid file type. Allowed: JPEG, PNG, GIF, WebP');
  }

  // Validate file size (max 10 MB)
  if (blob.size > MAX_IMAGE_SIZE_BYTES) {
    throw new Error(`File too large. Maximum size is ${MAX_IMAGE_SIZE_LABEL}`);
  }

  // Get presigned URL
  const { uploadUrl, publicUrl, key, storagePrefix } = await getPresignedUploadUrl(
    filename,
    contentType,
    options
  );

  // Upload to S3
  await uploadBlobToS3(uploadUrl, blob, contentType);

  return {
    url: publicUrl,
    key,
    filename,
    size: blob.size,
    contentType,
    storagePrefix,
  };
}
